import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Cropper from "react-easy-crop";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

/* ─── Google Fonts injection ─── */
const injectFonts = () => {
  if (document.getElementById("ep-fonts")) return;
  const link = document.createElement("link");
  link.id = "ep-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap";
  document.head.appendChild(link);
};

/* ─── CSS ─── Light / White Theme (Primary Blue) */
const styles = `
  /* Global reset to enable full-height layout */
  body, #root {
    margin: 0;
    padding: 0;
    height: 100%;
  }

  .ep-layout {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }

  :root {
    --ep-bg: #f8fafc;
    --ep-surface: #ffffff;
    --ep-surface2: #f1f5f9;
    --ep-border: rgba(0,0,0,0.08);
    --ep-primary: #3b82f6;
    --ep-primary-light: #60a5fa;
    --ep-primary-dim: rgba(59,130,246,0.08);
    --ep-text: #0f172a;
    --ep-muted: #475569;
    --ep-success: #10b981;
    --ep-danger: #ef4444;
    --ep-radius: 20px;
    --ep-font-display: 'Cormorant Garamond', serif;
    --ep-font-body: 'DM Sans', sans-serif;
  }

  .ep-wrapper {
    flex: 1;
    overflow-y: auto;
    background: var(--ep-bg);
    background-image: radial-gradient(circle at 10% 20%, rgba(59,130,246,0.02) 0%, transparent 60%);
    font-family: var(--ep-font-body);
    color: var(--ep-text);
    padding: 2rem 1rem 4rem;
  }

  .ep-container {
    max-width: 900px;
    margin: 0 auto;
  }

  /* ── Page title ── */
  .ep-page-title {
    font-family: var(--ep-font-display);
    font-size: 2.6rem;
    font-weight: 700;
    color: var(--ep-text);
    letter-spacing: -0.01em;
    margin-bottom: 0.2rem;
  }
  .ep-page-subtitle {
    font-size: 0.88rem;
    color: var(--ep-muted);
    font-weight: 400;
    letter-spacing: 0.02em;
  }
  .ep-title-bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 2.2rem;
    padding-bottom: 1.4rem;
    border-bottom: 1px solid var(--ep-border);
  }
  .ep-title-accent {
    width: 40px;
    height: 3px;
    background: linear-gradient(90deg, var(--ep-primary), rgba(59,130,246,0.3));
    border-radius: 2px;
    margin-top: 6px;
  }

  /* ── Main card ── */
  .ep-card {
    background: var(--ep-surface);
    border-radius: var(--ep-radius);
    overflow: hidden;
    box-shadow: 0 8px 30px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02);
    transition: box-shadow 0.2s ease;
    animation: epFadeUp 0.55s cubic-bezier(0.22,1,0.36,1) both;
  }
  .ep-card:hover {
    box-shadow: 0 20px 35px -12px rgba(0,0,0,0.08);
  }

  @keyframes epFadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ep-card-layout {
    display: grid;
    grid-template-columns: 260px 1fr;
  }

  /* ── Left sidebar ── */
  .ep-sidebar {
    background: #fefcf5;
    border-right: 1px solid var(--ep-border);
    padding: 2.5rem 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .ep-avatar-wrap {
    position: relative;
    width: 130px;
    height: 130px;
    border-radius: 50%;
    background: var(--ep-surface2);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }

  .ep-avatar-ring {
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: conic-gradient(var(--ep-primary), var(--ep-primary-light), var(--ep-primary));
    z-index: 0;
    animation: epRingSpin 6s linear infinite;
    opacity: 0.7;
  }

  @keyframes epRingSpin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  .ep-avatar-inner {
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    overflow: hidden;
    background: var(--ep-surface2);
    z-index: 1;
  }

  .ep-avatar-inner img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .ep-avatar-overlay {
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: rgba(0,0,0,0);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    transition: background 0.25s ease;
    cursor: pointer;
  }

  .ep-avatar-overlay:hover {
    background: rgba(0,0,0,0.4);
  }

  .ep-avatar-overlay svg, .ep-avatar-overlay i {
    opacity: 0;
    transition: opacity 0.25s ease;
    font-size: 1.4rem;
    color: #fff;
  }

  .ep-avatar-overlay:hover svg,
  .ep-avatar-overlay:hover i {
    opacity: 1;
  }

  .ep-sidebar-name {
    font-family: var(--ep-font-display);
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--ep-text);
    text-align: center;
    margin-top: 0.5rem;
  }

  .ep-sidebar-email {
    font-size: 0.78rem;
    color: var(--ep-muted);
    text-align: center;
    word-break: break-all;
  }

  .ep-sidebar-badge {
    margin-top: auto;
    background: var(--ep-primary-dim);
    border: 1px solid rgba(59,130,246,0.25);
    color: var(--ep-primary);
    font-size: 0.72rem;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 0.35rem 0.9rem;
    border-radius: 30px;
  }

  /* ── Right form area ── */
  .ep-form-area {
    padding: 2.5rem 2.2rem;
  }

  .ep-section-label {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ep-primary);
    margin-bottom: 1.2rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .ep-section-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--ep-border);
  }

  .ep-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem 1.4rem;
    margin-bottom: 1.8rem;
  }

  .ep-field {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }

  .ep-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--ep-muted);
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .ep-input-wrap {
    position: relative;
  }

  .ep-input {
    width: 100%;
    background: var(--ep-surface2);
    border: 1px solid var(--ep-border);
    border-radius: 14px;
    color: var(--ep-text);
    font-family: var(--ep-font-body);
    font-size: 0.9rem;
    padding: 0.7rem 0.9rem;
    outline: none;
    transition: all 0.2s ease;
    box-sizing: border-box;
  }

  .ep-input:focus {
    border-color: var(--ep-primary);
    box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
    background: white;
  }

  .ep-input[readonly] {
    opacity: 0.7;
    cursor: not-allowed;
    background: #f9fafb;
  }

  .ep-input.has-icon {
    padding-right: 2.6rem;
  }

  .ep-eye-btn {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--ep-muted);
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    transition: color 0.2s ease;
  }

  .ep-eye-btn:hover {
    color: var(--ep-primary);
  }

  /* ── Actions footer ── */
  .ep-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--ep-border);
    margin-top: 0.5rem;
  }

  .ep-btn {
    font-family: var(--ep-font-body);
    font-size: 0.85rem;
    font-weight: 500;
    border-radius: 12px;
    padding: 0.65rem 1.4rem;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    gap: 0.45rem;
    transition: all 0.2s ease;
    letter-spacing: 0.02em;
  }

  .ep-btn-ghost {
    background: transparent;
    border: 1px solid var(--ep-border);
    color: var(--ep-muted);
  }

  .ep-btn-ghost:hover {
    border-color: var(--ep-muted);
    color: var(--ep-text);
    background: rgba(0,0,0,0.02);
  }

  .ep-btn-primary {
    background: linear-gradient(135deg, var(--ep-primary) 0%, var(--ep-primary-light) 100%);
    color: white;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(59,130,246,0.25);
  }

  .ep-btn-primary:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(59,130,246,0.35);
    transform: translateY(-1px);
  }

  .ep-btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* ── Crop modal ── */
  .ep-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(255,255,255,0.85);
    backdrop-filter: blur(8px);
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: epFadeIn 0.2s ease;
  }

  @keyframes epFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .ep-modal {
    background: var(--ep-surface);
    border-radius: 24px;
    width: min(660px, 94vw);
    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.2);
    overflow: hidden;
    animation: epSlideUp 0.3s cubic-bezier(0.22,1,0.36,1);
  }

  @keyframes epSlideUp {
    from { transform: translateY(30px); opacity: 0; }
    to   { transform: translateY(0); opacity: 1; }
  }

  .ep-modal-header {
    padding: 1.2rem 1.6rem;
    border-bottom: 1px solid var(--ep-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ep-modal-title {
    font-family: var(--ep-font-display);
    font-size: 1.35rem;
    font-weight: 600;
    color: var(--ep-text);
  }

  .ep-modal-close {
    background: var(--ep-surface2);
    border: 1px solid var(--ep-border);
    color: var(--ep-muted);
    width: 32px; height: 32px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    font-size: 1rem;
    transition: all 0.2s;
  }

  .ep-modal-close:hover { color: var(--ep-text); background: #e9ecef; }

  .ep-crop-area {
    position: relative;
    width: 100%;
    height: 330px;
    background: #f1f5f9;
  }

  .ep-modal-controls {
    padding: 1.2rem 1.6rem;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  .ep-control-row {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .ep-control-label {
    font-size: 0.72rem;
    color: var(--ep-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    min-width: 52px;
  }

  .ep-range {
    -webkit-appearance: none;
    flex: 1;
    height: 4px;
    background: var(--ep-surface2);
    border-radius: 2px;
    outline: none;
    cursor: pointer;
  }

  .ep-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px; height: 16px;
    border-radius: 50%;
    background: var(--ep-primary);
    box-shadow: 0 0 4px rgba(59,130,246,0.3);
    cursor: pointer;
  }

  .ep-modal-footer {
    padding: 1rem 1.6rem;
    border-top: 1px solid var(--ep-border);
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
  }

  /* ── Spinner ── */
  .ep-spinner {
    width: 14px; height: 14px;
    border: 2px solid rgba(255,255,255,0.4);
    border-top-color: white;
    border-radius: 50%;
    animation: epSpin 0.7s linear infinite;
  }
  @keyframes epSpin { to { transform: rotate(360deg); } }

  /* ── Responsive ── */
  @media (max-width: 650px) {
    .ep-card-layout { grid-template-columns: 1fr; }
    .ep-sidebar {
      border-right: none;
      border-bottom: 1px solid var(--ep-border);
      flex-direction: row;
      padding: 1.4rem;
      gap: 1.2rem;
      align-items: center;
    }
    .ep-sidebar-badge { margin-top: 0; }
    .ep-avatar-wrap { width: 80px; height: 80px; flex-shrink: 0; }
    .ep-sidebar-info { display: flex; flex-direction: column; }
    .ep-grid { grid-template-columns: 1fr; }
    .ep-form-area { padding: 1.4rem; }
    .ep-page-title { font-size: 1.9rem; }
  }
`;

/* ─── Image helpers (unchanged) ─── */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (err) => reject(err));
    img.crossOrigin = "anonymous";
    img.src = url;
  });

const getRadianAngle = (deg) => (deg * Math.PI) / 180;

const rotateSize = (w, h, rotation) => {
  const rot = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rot) * w) + Math.abs(Math.sin(rot) * h),
    height: Math.abs(Math.sin(rot) * w) + Math.abs(Math.cos(rot) * h),
  };
};

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc);
  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(pixelCrop.width));
  canvas.height = Math.max(1, Math.round(pixelCrop.height));
  const ctx = canvas.getContext("2d");
  ctx.translate(-pixelCrop.x, -pixelCrop.y);
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) { reject(new Error("Canvas is empty")); return; }
        resolve(blob);
      },
      "image/jpeg",
      0.9
    );
  });
};
/* ─────────────────────── */

export default function AdminProfile() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [cropMode, setCropMode] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    injectFonts();
    const styleEl = document.createElement("style");
    styleEl.id = "ep-styles";
    styleEl.textContent = styles;
    document.head.appendChild(styleEl);
    return () => { styleEl.remove(); };
  }, []);

  useEffect(() => {
    if (!token) { toast.error("You must be logged in."); navigate("/"); }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/users/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (res.status === 401) { toast.error("Session expired."); navigate("/"); return null; } return res.json(); })
      .then((data) => { if (data?.email) setEmail(data.email); if (data?.name) setName(data.name); })
      .catch(() => toast.error("Failed to load profile"));
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/employees/me/photo`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!res.ok) throw new Error("No photo"); return res.blob(); })
      .then((blob) => {
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          setPhoto((prev) => { if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev); return url; });
        }
      })
      .catch(() => {});
    return () => { if (photo?.startsWith("blob:")) URL.revokeObjectURL(photo); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPhoto((prev) => { if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev); return url; });
    setPhotoFile(file);
    setCropMode(true);
    setZoom(1);
    setRotation(0);
  };

  const onCropComplete = useCallback((_, cap) => setCroppedAreaPixels(cap), []);

  const handleCropSave = async () => {
    if (!photo || !croppedAreaPixels) { toast.error("No image to crop"); return; }
    try {
      const blob = await getCroppedImg(photo, croppedAreaPixels, Number(rotation));
      const croppedFile = new File([blob], photoFile?.name || "photo.jpg", { type: "image/jpeg" });
      const newUrl = URL.createObjectURL(blob);
      setPhoto((prev) => { if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev); return newUrl; });
      setPhotoFile(croppedFile);
      setCropMode(false);
      toast.success("Image cropped — click Update Profile to save.");
    } catch (err) {
      toast.error("Failed to crop image.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (oldPassword && newPassword && oldPassword === newPassword) {
      toast.error("New password cannot match old password.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, oldPassword, newPassword }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || "Profile updated!"); navigate("/admindashboard"); }
      else toast.error(data.error || "Update failed");
    } catch {
      toast.error("Error updating profile");
    }

    if (photoFile) {
      const formData = new FormData();
      formData.append("photo", photoFile);
      try {
        const res = await fetch(`${API_BASE_URL}/api/employees/me/photo`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (res.ok) toast.success("Photo updated!");
        else toast.error("Failed to update photo");
      } catch { toast.error("Error uploading photo"); }
    }
    setLoading(false);
  };

  const placeholder = "https://ui-avatars.com/api/?name=" + encodeURIComponent(name || "User") + "&background=f1f5f9&color=3b82f6&size=200&bold=true";

  return (
    <div className="ep-layout">
      <AdminNavbar />

      {/* Crop modal */}
      {cropMode && (
        <div className="ep-modal-backdrop">
          <div className="ep-modal">
            <div className="ep-modal-header">
              <span className="ep-modal-title">Adjust Photo</span>
              <button className="ep-modal-close" onClick={() => setCropMode(false)}>✕</button>
            </div>

            <div className="ep-crop-area">
              <Cropper
                image={photo}
                crop={crop}
                zoom={zoom}
                rotation={Number(rotation)}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={(z) => setZoom(Number(z))}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="ep-modal-controls">
              <div className="ep-control-row">
                <span className="ep-control-label">Zoom</span>
                <input type="range" className="ep-range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
              </div>
              <div className="ep-control-row">
                <span className="ep-control-label">Rotate</span>
                <input type="range" className="ep-range" min={0} max={360} step={1} value={rotation} onChange={(e) => setRotation(Number(e.target.value))} />
              </div>
            </div>

            <div className="ep-modal-footer">
              <button className="ep-btn ep-btn-ghost" onClick={() => setCropMode(false)}>Cancel</button>
              <button className="ep-btn ep-btn-primary" onClick={handleCropSave}>Apply Crop</button>
            </div>
          </div>
        </div>
      )}

      <div className="ep-wrapper">
        <div className="ep-container">

          {/* Page heading */}
          <div className="ep-title-bar">
            <div>
              <h1 className="ep-page-title">Manage Profile</h1>
              <div className="ep-title-accent" />
              <p className="ep-page-subtitle">Update your personal information &amp; credentials</p>
            </div>
          </div>

          {/* Card */}
          <div className="ep-card">
            <div className="ep-card-layout">

              {/* Sidebar */}
              <div className="ep-sidebar">
                <label htmlFor="fileUpload" style={{ cursor: "pointer" }}>
                  <div className="ep-avatar-wrap">
                    <div className="ep-avatar-ring" />
                    <div className="ep-avatar-inner">
                      <img src={photo || placeholder} alt="Profile" />
                    </div>
                    <div className="ep-avatar-overlay">
                      <i className="bi bi-camera-fill" />
                    </div>
                  </div>
                </label>
                <input id="fileUpload" type="file" accept="image/*" hidden onChange={handleFileChange} />

                <div className="ep-sidebar-info">
                  {name && <div className="ep-sidebar-name">{name}</div>}
                  <div className="ep-sidebar-email">{email}</div>
                </div>

                <div className="ep-sidebar-badge">Admin</div>
              </div>

              {/* Form area */}
              <div className="ep-form-area">
                <form onSubmit={handleSubmit}>

                  {/* Account info */}
                  <div className="ep-section-label">Account Information</div>
                  <div className="ep-grid">
                    <div className="ep-field">
                      <label className="ep-label">Full Name</label>
                      <div className="ep-input-wrap">
                        <input
                          type="text"
                          className="ep-input"
                          placeholder="Your full name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="ep-field">
                      <label className="ep-label">Email Address</label>
                      <div className="ep-input-wrap">
                        <input
                          type="email"
                          className="ep-input"
                          value={email}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {/* Security */}
                  <div className="ep-section-label">Security</div>
                  <div className="ep-grid">
                    <div className="ep-field">
                      <label className="ep-label">Current Password</label>
                      <div className="ep-input-wrap">
                        <input
                          type={showOldPassword ? "text" : "password"}
                          className="ep-input has-icon"
                          placeholder="Enter current password"
                          value={oldPassword}
                          onChange={(e) => setOldPassword(e.target.value)}
                        />
                        <button type="button" className="ep-eye-btn" onClick={() => setShowOldPassword(!showOldPassword)}>
                          {showOldPassword ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                    </div>

                    <div className="ep-field">
                      <label className="ep-label">New Password</label>
                      <div className="ep-input-wrap">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          className="ep-input has-icon"
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <button type="button" className="ep-eye-btn" onClick={() => setShowNewPassword(!showNewPassword)}>
                          {showNewPassword ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Footer actions */}
                  <div className="ep-footer">

                    <button type="submit" className="ep-btn ep-btn-primary" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="ep-spinner" />
                          Saving…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg" />
                          Update Profile
                        </>
                      )}
                    </button>
                    
                    <button type="button" className="ep-btn ep-btn-ghost" onClick={() => navigate(-1)}>
                      Cancel
                    </button>
                    
                  </div>

                </form>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}